import { COLORLIST } from 'src/app/core/constants';

/*
 * Comments before playing, for fans who already watched the album (spec video-detail-redesign US-14).
 * Pure helpers; the formatting matches the album-video player's comments (getColor / getName / postComment).
 */

const WATCHED_KEY = 'vd.watchedAlbums';
const MAX_REMEMBERED = 200;

function readWatched(): number[] {
  try {
    const list = JSON.parse(localStorage.getItem(WATCHED_KEY) || '[]');
    return Array.isArray(list) ? list.map(Number).filter(id => id > 0) : [];
  } catch {
    return [];
  }
}

/** Remembers on this browser that a video of the album was played (newest first, capped). Best effort. */
export function rememberWatchedAlbum(albumId: any): void {
  const id = Number(albumId);
  if (!(id > 0)) return;
  try {
    const list = [id, ...readWatched().filter(x => x !== id)].slice(0, MAX_REMEMBERED);
    localStorage.setItem(WATCHED_KEY, JSON.stringify(list));
  } catch {
    // Storage blocked or full: behaves like a first visit.
  }
}

export function wasAlbumWatched(albumId: any): boolean {
  const id = Number(albumId);
  return id > 0 && readWatched().includes(id);
}

/** The album's comments channel, as the player builds it: one per album, named after its first video. */
export function albumCommentsChannel(firstVideo: any): string {
  return firstVideo?.id ? `${firstVideo.id}_video_comments` : '';
}

/** Same colour as the player's getColor: a hash of the sender id over COLORLIST. */
export function commentColor(payload: any): string {
  const userId = payload?._sender?.userId ?? '';
  if (!userId) return COLORLIST[0];
  let hash = 0;
  for (let i = 0; i < userId.length; i++) hash = (hash + userId.charCodeAt(i)) % COLORLIST.length;
  return COLORLIST[hash];
}

/** Same initials as the player's getName: first letters of two words, else the first two letters. */
export function commentInitials(name: string): string {
  const parts = String(name || '').split(' ').filter(Boolean);
  if (!parts.length) return '';
  return parts.length > 1 ? parts[0].charAt(0) + parts[1].charAt(0) : parts[0].slice(0, 2);
}

/** Display name for a new comment: display name, else first + last name, else "Fan". */
export function commentAuthorName(userInfo: any): string {
  const full = `${userInfo?.user_first_name || ''} ${userInfo?.user_last_name || ''}`.trim();
  return (userInfo?.user_display_name || '').trim() || full || 'Fan';
}

/** A PubNub comment payload → the item the Comments list shows. */
export function toCommentItem(payload: any): any {
  return {
    _sender: { ...(payload?._sender || {}), userColor: commentColor(payload), shortName: commentInitials(payload?._sender?.nickname || '') },
    message: payload?.message || '',
    messageId: payload?.messageId,
    time: new Date(payload?.timestamp),
    timestamp: payload?.timestamp,
  };
}

/** Newest first, without repeats (the sender's own comment comes back from the subscription too). */
export function addCommentItem(list: any[], payload: any): any[] {
  if (payload?.messageId && (list || []).some(c => c.messageId === payload.messageId)) return list;
  return [toCommentItem(payload), ...(list || [])];
}
